import { useEffect, useMemo } from "react";

import type { ResidentEngagementMonthlyV2Dto, ResidentTimeEngagementDto } from "@/api/types";
import { useMetricHelp } from "@/components/Analytics/useMetricHelp";
import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ChartLegend } from "@/components/Charts/ChartLegend";
import { dataColors } from "@/components/Charts/theme";
import { parseChartDate } from "@/components/Charts/util";
import { NoData } from "@/components/NoData/NoData";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { Stat } from "@/components/Stat/Stat";
import { Inline as LayoutInline } from "@/design-system/Inline";
import { Notice } from "@/design-system/Notice";
import { Select } from "@/design-system/Select";
import { Spinner } from "@/design-system/Spinner";
import { Stack } from "@/design-system/Stack";
import { isDefined } from "@/helpers/util";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useScrollToPageTop } from "@/hooks/useScrollToPageTop";
import { useSlug } from "@/hooks/useSlug";
import { AnalyticsCard } from "@/modules/analytics/components/AnalyticsCard";
import { BarChart } from "@/modules/analytics/components/BarChart";
import type { DateRangeOption } from "@/modules/analytics/util";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import type { NonEmptyArray } from "@/types/utility-types";

export interface EngagementAnalyticsDetailsProps {
  engagementDetails: ResidentEngagementMonthlyV2Dto | undefined;
  engagementStats: ResidentTimeEngagementDto[];
  isLoading: boolean;
  selectedDateRange: DateRangeOption;
  dateRangeOptions: NonEmptyArray<DateRangeOption>;
  onDateRangeChange: (dateRange: DateRangeOption) => void;
}
export function EngagementAnalyticsDetails({
  engagementDetails,
  engagementStats,
  isLoading,
  selectedDateRange,
  dateRangeOptions,
  onDateRangeChange,
}: EngagementAnalyticsDetailsProps): React.ReactNode {
  const slug = useSlug();
  const projectId = useProjectId();
  const { t } = useTranslation();
  const help = useMetricHelp();
  const scrollToTop = useScrollToPageTop();
  useEffect(() => {
    scrollToTop("instant");
  }, [scrollToTop]);
  const adoptionDataPoints = useMemo(
    () =>
      engagementDetails?.data?.map((point) => ({
        x: parseChartDate(point.range).toISOString(),
        y: point.percentage,
        isPending: point.isCurrentMonth,
      })) || [],
    [engagementDetails],
  );
  const benchmark = useMemo(
    () =>
      engagementDetails?.benchmark?.map((point) => ({
        x: parseChartDate(point.range).toISOString(),
        y: point.percentage,
      })) || [],
    [engagementDetails],
  );
  const noEngagementDataAvailable = !engagementDetails?.data || engagementDetails.data.every((x) => x.percentage === 0);
  const last24HoursEngagement = engagementStats.find((x) => x.type === "last24Hours");
  const last7DaysEngagement = engagementStats.find((x) => x.type === "last7Days");

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.analytics-details.engagement.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.analytics-overview.overview.title"),
              to: routes.analytics.overview({
                slug,
              }),
            },
            {
              name: t("page.analytics-details.engagement.title"),
            },
          ]}
        />
      }
      actions={
        <AskNobiButton
          defaultFilters={{ projectIds: [projectId] }}
          context={{
            scope: "project",
            entity: "analytics engagement",
            page: "detail",
          }}
          suggestions={[
            t("component.ask-nobi-chat-drawer.analytics-engagement.suggestion.1"),
            t("component.ask-nobi-chat-drawer.analytics-engagement.suggestion.2"),
          ]}
        />
      }
      header={
        <LayoutInline gap="sm" align="center">
          <span className="text-body-emphasis">{t("page.analytics-details.time-range.label")}</span>
          <Select
            items={dateRangeOptions}
            selected={selectedDateRange}
            onChange={onDateRangeChange}
            renderOption={(x) => x.name}
            keySelector={(x) => x.value}
          />
        </LayoutInline>
      }
    >
      <PageGrid.Row>
        <PageGrid.Column size="66%" isDesktopIndifferent>
          <AnalyticsCard.Root height="content">
            <AnalyticsCard.Header
              title={t("page.analytics-details.engagement.chart.engagement.title")}
              tooltip={help("page.analytics-details.engagement.chart.engagement.title.tooltip")}
            />
            {isLoading && (
              <LayoutInline justify="center">
                <Spinner size="xl" />
              </LayoutInline>
            )}
            {!isLoading && noEngagementDataAvailable && <NoData error={noEngagementDataAvailable} />}
            {!isLoading && !noEngagementDataAvailable && (
              <Stack gap="md">
                <span className="text-caption text-text-secondary">
                  {t("page.analytics-details.engagement.chart.engagement.description")}
                </span>
                <figure className="flex max-h-screen-minus-64 min-h-scroll-area-standard w-full flex-col">
                  <BarChart
                    dataPrimary={{
                      data: adoptionDataPoints,
                    }}
                    dataBenchmark={benchmark}
                    xAxisType="date"
                    maxY={100}
                    formatYTick={(y) => `${y}%`}
                    hoverLabel={(data) => (
                      <Stack>
                        <span>
                          {t("page.analytics-details.engagement.chart.tooltip.primary", {
                            percentage: data.primary?.y || 0,
                          })}
                        </span>
                        {isDefined(data.benchmark) && (
                          <span>
                            {t("page.analytics-details.engagement.chart.tooltip.benchmark", {
                              percentage: data.benchmark.y || 0,
                            })}
                          </span>
                        )}
                        {data.primary?.isPending && (
                          <span>
                            {t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}
                          </span>
                        )}
                      </Stack>
                    )}
                  />
                  <ChartLegend
                    items={[
                      {
                        label: t("page.analytics-details.engagement.chart.engagement.legend.primary"),
                        color: dataColors.primary,
                      },
                      {
                        label: t("common.label.aop-benchmark"),
                        color: dataColors.benchmark,
                      },
                      {
                        label: t("page.analytics-details.engagement.chart.legend.current-period"),
                        color: dataColors.primary,
                        pattern: "striped",
                      },
                    ]}
                  />
                </figure>
                <section className="flex w-full gap-space-md">
                  {last24HoursEngagement && (
                    <Stat
                      label={t("page.analytics-details.engagement.card.day")}
                      value={last24HoursEngagement.engagementPercentage}
                      renderValue={(value) => `${value}%`}
                    />
                  )}
                  {last7DaysEngagement && (
                    <Stat
                      label={t("page.analytics-details.engagement.card.week")}
                      value={last7DaysEngagement.engagementPercentage}
                      renderValue={(value) => `${value}%`}
                    />
                  )}
                </section>
                <Notice variant="special" message={t("page.analytics-details.engagement.chart.engagement.notice")} />
              </Stack>
            )}
          </AnalyticsCard.Root>
        </PageGrid.Column>

        <PageGrid.Column size="33%" isDesktopIndifferent>
          <Stack gap="md">
            <AnalyticsCard.Root>
              <AnalyticsCard.Header title={t("page.analytics-details.engagement.information.title")} />
              <span className="text-body text-text-secondary">
                {t("page.analytics-details.engagement.information.subtitle")}
              </span>
              <ul className="list-disc px-space-md">
                {t("page.analytics-details.engagement.information.content")
                  .split("\n")
                  .map((item, i) => item.length > 0 && <li key={i}>{item}</li>)}
              </ul>
            </AnalyticsCard.Root>
            <AnalyticsCard.Root>
              <AnalyticsCard.Header title={t("page.analytics-details.engagement.best-practices.title")} />
              <span className="text-body text-text-secondary">
                {t("page.analytics-details.engagement.best-practices.subtitle")}
              </span>
              <ul className="list-disc px-space-md">
                {t("page.analytics-details.engagement.best-practices.content")
                  .split("\n")
                  .map((item, i) => item.length > 0 && <li key={i}>{item}</li>)}
              </ul>
            </AnalyticsCard.Root>
          </Stack>
        </PageGrid.Column>
      </PageGrid.Row>
    </DocumentPage>
  );
}
